import { UserPlus } from "lucide-react";
import React, { useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { CreatedClientSummary } from "@/modules/dashboard/clients/components/create/ClientSuccessStep";
import CreateClientModal from "@/modules/dashboard/clients/components/CreateClientModal";
import type { ClientAccount } from "@/modules/dashboard/clients/types/clients-api.types";
import { canWriteClients } from "@/modules/dashboard/clients/utils/clientsPermissions";
import useTranslations from "@/shared/lang/useTranslations";

export interface RegisterClientActionProps {
  /** Destination currency of the dispersion — locks the optional account step to it. */
  destinationCurrency: string;
  /** Fired as soon as the client exists (the drawer stays open on its success step). */
  onClientCreated: (client: CreatedClientSummary) => void;
  /** Fired right before the drawer auto-closes after the optional account was created. */
  onAccountCreated: (client: CreatedClientSummary, account: ClientAccount) => void;
}

/**
 * "Register client" shortcut for the recipient step — the right-aligned label action of the client select.
 * Opens the same create-client drawer as /app/clients (client branch only) with the optional account step
 * locked to the destination currency, so the new client is usable in this dispersion immediately. Hidden
 * without the clients write permission.
 */
export const RegisterClientAction: React.FC<RegisterClientActionProps> = ({
  destinationCurrency,
  onClientCreated,
  onAccountCreated,
}) => {
  const { user } = useAuth();
  const { t } = useTranslations("dispersions");
  const [isOpen, setIsOpen] = useState(false);

  if (!canWriteClients(user)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/90"
      >
        <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
        {t("register_client_button")}
      </button>
      <CreateClientModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        clientOnly
        onClientCreated={onClientCreated}
        successAccountCurrency={destinationCurrency}
        onAccountCreated={onAccountCreated}
      />
    </>
  );
};
